import { useSite } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { Reveal } from "@/components/Reveal";
import Img from "@/components/Img";

/**
 * A quiet, slow-moving band of partner logos. Muted at rest, the logo under the
 * cursor comes up to full strength. Falls back to a static row when the visitor
 * has asked for reduced motion.
 */
export const PartnerWall = () => {
  const { t, L } = useLang();
  const site = useSite();
  const spotlight = site?.settings?.spotlight;
  const anchorName = spotlight?.enabled ? (spotlight.name || "").toLowerCase() : "";

  // the anchor partner leads the strip and keeps its real colours
  const partners = [...(site?.partners || [])].sort((a, b) => {
    const rank = (p) => (anchorName && (p.name || "").toLowerCase().includes(anchorName) ? 0 : 1);
    return rank(a) - rank(b);
  });

  if (partners.length === 0) return null;

  // pad short lists, then mirror the block so the -50% loop has no visible seam
  const block = [];
  while (block.length < 4) block.push(...partners);
  const track = [...block, ...block];

  return (
    <section className="border-t border-border bg-card/20" data-testid="partner-wall">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-20">
        <Reveal>
          <p className="eyebrow text-center">{t("partners_eyebrow")}</p>
          <h2 className="mt-3 text-center font-head text-3xl md:text-4xl">{t("partners_title")}</h2>
        </Reveal>

        <div className="partner-marquee mt-12" data-testid="partner-marquee">
          <div className="partner-track">
            {track.map((p, i) => {
              const isAnchor = !!anchorName && (p.name || "").toLowerCase().includes(anchorName);
              const inner = (
                <>
                  <Img src={p.logo} width={320} sizes="200px" alt={p.name} />
                  <span className="partner-name">
                    {isAnchor ? `${t("spotlight_footer")} — ${p.name}` : p.name}
                  </span>
                </>
              );
              const title = L(p.note) || p.name;
              return p.link ? (
                <a
                  key={`${p.id}-${i}`}
                  href={p.link}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={title}
                  aria-label={p.name}
                  className={`partner-cell${isAnchor ? " is-anchor" : ""}`}
                  data-testid={i === 0 || (i === 1 && partners.length > 1) ? `partner-${p.id}` : undefined}
                >
                  {inner}
                </a>
              ) : (
                <span
                  key={`${p.id}-${i}`}
                  title={title}
                  className={`partner-cell${isAnchor ? " is-anchor" : ""}`}
                  data-testid={i === 0 || (i === 1 && partners.length > 1) ? `partner-${p.id}` : undefined}
                >
                  {inner}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerWall;
